import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Shield, 
  Users, 
  Trophy,
  TrendingUp,
  Search,
  ChevronLeft,
  ChevronRight,
  Globe,
  Activity,
  Calendar,
  Target
} from 'lucide-react';
import { useAdmin } from '../contexts/AdminContext';
import { adminAPI } from '../services/api';
import toast from 'react-hot-toast';
import QuickLogin from '../components/QuickLogin';
import AdminNavigation from '../components/AdminNavigation';

const HltvAdminPage = () => {
  const [hltvStats, setHltvStats] = useState(null);
  const [teams, setTeams] = useState([]);
  const [players, setPlayers] = useState([]);
  const [rankings, setRankings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Pagination states
  const [teamsPage, setTeamsPage] = useState(1);
  const [playersPage, setPlayersPage] = useState(1);
  const [rankingsPage, setRankingsPage] = useState(1);
  const [teamsPagination, setTeamsPagination] = useState({});
  const [playersPagination, setPlayersPagination] = useState({});
  const [rankingsPagination, setRankingsPagination] = useState({});
  
  // Search states
  const [playersSearch, setPlayersSearch] = useState('');
  const [playersCountry, setPlayersCountry] = useState('');
  const [rankingsDate, setRankingsDate] = useState('');

  const { isAuthenticated } = useAdmin();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin/login');
      return;
    }
    loadData();
  }, [isAuthenticated, navigate]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load overview data
      const [statsResponse] = await Promise.all([
        adminAPI.getHltvStats()
      ]);
      
      setHltvStats(statsResponse.data?.data || {
        total_teams: 0,
        active_teams: 0,
        total_players: 0,
        active_players: 0,
        total_rankings: 0,
        latest_ranking_date: null,
        top_teams: [],
        country_distribution: []
      });
      
      // Load initial data for each tab
      await Promise.all([
        loadTeams(1),
        loadPlayers(1, '', ''),
        loadRankings(1, '')
      ]);
      
    } catch (error) {
      toast.error('Failed to load HLTV data');
      console.error('Error loading HLTV data:', error);
      // Set default values on error
      setHltvStats({
        total_teams: 0,
        active_teams: 0,
        total_players: 0,
        active_players: 0,
        total_rankings: 0,
        latest_ranking_date: null,
        top_teams: [],
        country_distribution: []
      });
    } finally {
      setLoading(false);
    }
  };

  const loadTeams = async (page = 1) => {
    try {
      const response = await adminAPI.getHltvTeams(page, 1000);
      setTeams(response.data?.data || []);
      setTeamsPagination(response.data?.pagination || { page: 1, limit: 1000, total: 0, pages: 0 });
      setTeamsPage(page);
    } catch (error) {
      toast.error('Failed to load teams');
      setTeams([]);
      setTeamsPagination({ page: 1, limit: 1000, total: 0, pages: 0 });
    }
  };

  const loadPlayers = async (page = 1, search = '', country = '') => {
    try {
      const response = await adminAPI.getHltvPlayers(page, 1000, search, country);
      setPlayers(response.data?.data || []);
      setPlayersPagination(response.data?.pagination || { page: 1, limit: 1000, total: 0, pages: 0 });
      setPlayersPage(page);
    } catch (error) {
      toast.error('Failed to load players');
      setPlayers([]);
      setPlayersPagination({ page: 1, limit: 1000, total: 0, pages: 0 });
    }
  };

  const loadRankings = async (page = 1, date = '') => {
    try {
      const response = await adminAPI.getHltvRankings(page, 1000, date);
      setRankings(response.data?.data || []);
      setRankingsPagination(response.data?.pagination || { page: 1, limit: 1000, total: 0, pages: 0 });
      setRankingsPage(page);
    } catch (error) {
      toast.error('Failed to load rankings');
      setRankings([]);
      setRankingsPagination({ page: 1, limit: 1000, total: 0, pages: 0 });
    }
  };

  const handlePlayersSearch = () => {
    loadPlayers(1, playersSearch, playersCountry);
  };

  const handleRankingsFilter = () => {
    loadRankings(1, rankingsDate);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const renderPagination = (pagination, onPageChange) => {
    if (!pagination || pagination.pages <= 1) return null;

    const pages = [];
    const currentPage = pagination.page;
    const totalPages = pagination.pages;

    // Show previous button
    if (currentPage > 1) {
      pages.push(
        <button
          key="prev"
          onClick={() => onPageChange(currentPage - 1)}
          className="px-3 py-1 rounded bg-gray-700 text-white hover:bg-gray-600 flex items-center space-x-1"
        >
          <ChevronLeft className="h-4 w-4" />
          <span>Previous</span>
        </button>
      );
    }

    // Show page numbers (simplified for space)
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, currentPage + 2);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => onPageChange(i)}
          className={`px-3 py-1 rounded ${
            currentPage === i
              ? 'bg-primary-600 text-white'
              : 'bg-gray-700 text-white hover:bg-gray-600'
          }`}
        >
          {i}
        </button>
      );
    }

    // Show next button
    if (currentPage < totalPages) {
      pages.push(
        <button
          key="next"
          onClick={() => onPageChange(currentPage + 1)}
          className="px-3 py-1 rounded bg-gray-700 text-white hover:bg-gray-600 flex items-center space-x-1"
        >
          <span>Next</span>
          <ChevronRight className="h-4 w-4" />
        </button>
      );
    }

    return (
      <div className="flex justify-center space-x-2 mt-4">
        {pages}
      </div>
    );
  };

  if (loading) {
    return (
      <>
        <AdminNavigation />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="loading-spinner" />
        </div>
      </>
    );
  }

  return (
    <>
      <AdminNavigation />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-8">
          <QuickLogin />
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center space-x-4"
      >
        <div className="p-3 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg">
          <Trophy className="h-8 w-8 text-white" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-white">HLTV Data Dashboard</h1>
          <p className="text-gray-400">Team rankings, players, and statistics</p>
        </div>
      </motion.div>

      {/* Tabs */}
      <div className="flex space-x-1 bg-gray-800 p-1 rounded-lg">
        {[
          { id: 'overview', label: 'Overview', icon: Activity },
          { id: 'teams', label: 'Teams', icon: Users },
          { id: 'players', label: 'Players', icon: Target },
          { id: 'rankings', label: 'Rankings', icon: TrendingUp }
        ].map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-colors ${
              activeTab === id
                ? 'bg-primary-600 text-white'
                : 'text-gray-400 hover:text-white hover:bg-gray-700'
            }`}
          >
            <Icon className="h-4 w-4" />
            <span>{label}</span>
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && hltvStats && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="card p-6">
              <div className="flex items-center space-x-3">
                <Users className="h-8 w-8 text-blue-400" />
                <div>
                  <div className="text-2xl font-bold text-white">{hltvStats.total_teams || 0}</div>
                  <div className="text-sm text-gray-400">Total Teams</div>
                  <div className="text-xs text-green-400">{hltvStats.active_teams || 0} active</div>
                </div>
              </div>
            </div>

            <div className="card p-6">
              <div className="flex items-center space-x-3">
                <Target className="h-8 w-8 text-green-400" />
                <div>
                  <div className="text-2xl font-bold text-white">{hltvStats.total_players || 0}</div>
                  <div className="text-sm text-gray-400">Total Players</div>
                  <div className="text-xs text-green-400">{hltvStats.active_players || 0} active</div>
                </div>
              </div>
            </div>

            <div className="card p-6">
              <div className="flex items-center space-x-3">
                <TrendingUp className="h-8 w-8 text-purple-400" />
                <div>
                  <div className="text-2xl font-bold text-white">{hltvStats.total_rankings || 0}</div>
                  <div className="text-sm text-gray-400">Total Rankings</div>
                  <div className="text-xs text-yellow-400">
                    {hltvStats.latest_ranking_date ? formatDate(hltvStats.latest_ranking_date) : 'No data'}
                  </div>
                </div>
              </div>
            </div>

            <div className="card p-6">
              <div className="flex items-center space-x-3">
                <Globe className="h-8 w-8 text-orange-400" />
                <div>
                  <div className="text-2xl font-bold text-white">{hltvStats.country_distribution?.length || 0}</div>
                  <div className="text-sm text-gray-400">Countries</div>
                  <div className="text-xs text-blue-400">Represented</div>
                </div>
              </div>
            </div>
          </div>

          {/* Top Teams */}
          {hltvStats.top_teams.length > 0 && (
            <div className="card p-6">
              <h3 className="text-xl font-bold text-white mb-4 flex items-center space-x-2">
                <Trophy className="h-5 w-5 text-yellow-400" />
                <span>Top Ranked Teams</span>
              </h3>
              <div className="space-y-3">
                {hltvStats.top_teams.map((team, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-700 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center text-white font-bold">
                        #{team.rank}
                      </div>
                      <span className="text-white font-medium">{team.name}</span>
                    </div>
                    <div className="text-primary-400 font-bold">{team.points} pts</div>
                  </div>
                ))}
              </div>
            </div>
          )}

        </motion.div>
      )}

      {/* Teams Tab */}
      {activeTab === 'teams' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card p-6"
        >
          <h3 className="text-xl font-bold text-white mb-6 flex items-center space-x-2">
            <Users className="h-5 w-5 text-blue-400" />
            <span>HLTV Teams ({teamsPagination.total || 0})</span>
          </h3>

          {teams.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-16 w-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">No teams data available</p>
              <p className="text-sm text-gray-500 mt-2">Run the HLTV crawler to populate data</p>
            </div>
          ) : (
            <>
              <div className="space-y-4">
                {teams.map((team) => (
                  <motion.div
                    key={team.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-gray-800 rounded-lg p-4 hover:bg-gray-700 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-3">
                          <span className="text-2xl font-bold text-primary-400">#{team.current_rank || 'N/A'}</span>
                          {team.logo_url && (
                            <img src={team.logo_url} alt={team.name} className="w-12 h-12 rounded" />
                          )}
                          <div>
                            <div className="text-xl font-bold text-white">{team.name}</div>
                            <div className="text-sm text-gray-400">
                              {team.hltv_points} points • 
                              <span className={`ml-1 ${
                                team.ranking_change === '-' ? 'text-gray-400' :
                                team.ranking_change.startsWith('+') ? 'text-green-400' : 'text-red-400'
                              }`}>
                                {team.ranking_change}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`text-xs px-2 py-1 rounded ${
                          team.is_active ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
                        }`}>
                          {team.is_active ? 'Active' : 'Inactive'}
                        </span>
                        <span className="text-xs text-gray-400">ID: {team.hltv_team_id}</span>
                      </div>
                    </div>
                    
                    <div>
                      <div className="text-sm text-gray-300 mb-2 font-medium">Players:</div>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                        {team.players.map((player) => (
                          <div key={player.id} className="flex items-center space-x-2 bg-gray-700 rounded-lg p-2">
                            {player.image_url && (
                              <img 
                                src={player.image_url} 
                                alt={player.nickname} 
                                className="w-8 h-8 rounded-full object-cover"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            )}
                            <div className="min-w-0 flex-1">
                              <div className="text-white text-sm font-medium truncate">{player.nickname}</div>
                              <div className="text-xs text-gray-400 flex items-center">
                                <span className="mr-1">{player.country_code}</span>
                                <span className="truncate">{player.real_name}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                        {team.players.length < 5 && (
                          <div className="text-xs text-red-400 col-span-full">
                            ⚠️ Missing {5 - team.players.length} player(s)
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
              {renderPagination(teamsPagination, loadTeams)}
            </>
          )}
        </motion.div>
      )}

      {/* Players Tab */}
      {activeTab === 'players' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-white flex items-center space-x-2">
              <Target className="h-5 w-5 text-green-400" />
              <span>HLTV Players ({playersPagination.total || 0})</span>
            </h3>

            {/* Search Controls */}
            <div className="flex items-center space-x-3">
              <div className="relative">
                <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search players..."
                  value={playersSearch}
                  onChange={(e) => setPlayersSearch(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handlePlayersSearch()}
                  className="pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-primary-500"
                />
              </div>
              <input
                type="text"
                placeholder="Country code..."
                value={playersCountry}
                onChange={(e) => setPlayersCountry(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handlePlayersSearch()}
                className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-primary-500"
              />
              <button
                onClick={handlePlayersSearch}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                Filter
              </button>
            </div>
          </div>

          {players.length === 0 ? (
            <div className="text-center py-12">
              <Target className="h-16 w-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">No players data available</p>
              <p className="text-sm text-gray-500 mt-2">Run the HLTV crawler to populate data</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className="text-left py-3 px-4 text-gray-300">Player</th>
                      <th className="text-left py-3 px-4 text-gray-300">Real Name</th>
                      <th className="text-left py-3 px-4 text-gray-300">Country</th>
                      <th className="text-left py-3 px-4 text-gray-300">Current Team</th>
                      <th className="text-left py-3 px-4 text-gray-300">HLTV ID</th>
                      <th className="text-left py-3 px-4 text-gray-300">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {players.map((player) => (
                      <tr key={player.id} className="border-b border-gray-800 hover:bg-gray-800">
                        <td className="py-3 px-4">
                          <div className="flex items-center space-x-3">
                            {player.image_url && (
                              <img src={player.image_url} alt={player.nickname} className="w-8 h-8 rounded-full" />
                            )}
                            <span className="text-white font-medium">{player.nickname}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-gray-300">{player.real_name || 'N/A'}</td>
                        <td className="py-3 px-4">
                          <div className="flex items-center space-x-2">
                            <span className="text-lg">{player.country_code || 'N/A'}</span>
                            <span className="text-gray-300 text-xs">{player.country_name}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          {player.current_team ? (
                            <div>
                              <span className="text-white">{player.current_team.name}</span>
                              <span className="text-xs text-gray-400 ml-2">#{player.current_team.current_rank}</span>
                            </div>
                          ) : (
                            <span className="text-gray-400">Free agent</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-gray-400">{player.hltv_player_id || 'N/A'}</td>
                        <td className="py-3 px-4">
                          <span className={`text-xs px-2 py-1 rounded ${
                            player.is_active ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
                          }`}>
                            {player.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {renderPagination(playersPagination, (page) => loadPlayers(page, playersSearch, playersCountry))}
            </>
          )}
        </motion.div>
      )}

      {/* Rankings Tab */}
      {activeTab === 'rankings' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-white flex items-center space-x-2">
              <TrendingUp className="h-5 w-5 text-purple-400" />
              <span>HLTV Rankings ({rankingsPagination.total || 0})</span>
            </h3>

            {/* Date Filter */}
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2">
                <Calendar className="h-4 w-4 text-gray-400" />
                <input
                  type="date"
                  value={rankingsDate}
                  onChange={(e) => setRankingsDate(e.target.value)}
                  className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-primary-500"
                />
              </div>
              <button
                onClick={handleRankingsFilter}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                Filter
              </button>
              <button
                onClick={() => {
                  setRankingsDate('');
                  loadRankings(1, '');
                }}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-500"
              >
                Clear
              </button>
            </div>
          </div>

          {rankings.length === 0 ? (
            <div className="text-center py-12">
              <TrendingUp className="h-16 w-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">No rankings data available</p>
              <p className="text-sm text-gray-500 mt-2">Run the HLTV crawler to populate data</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className="text-left py-3 px-4 text-gray-300">Rank</th>
                      <th className="text-left py-3 px-4 text-gray-300">Team</th>
                      <th className="text-left py-3 px-4 text-gray-300">Points</th>
                      <th className="text-left py-3 px-4 text-gray-300">Change</th>
                      <th className="text-left py-3 px-4 text-gray-300">Date</th>
                      <th className="text-left py-3 px-4 text-gray-300">HLTV ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rankings.map((ranking) => (
                      <tr key={ranking.id} className="border-b border-gray-800 hover:bg-gray-800">
                        <td className="py-3 px-4">
                          <span className="font-bold text-primary-400">#{ranking.rank_position}</span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center space-x-3">
                            {ranking.team.logo_url && (
                              <img src={ranking.team.logo_url} alt={ranking.team.name} className="w-8 h-8 rounded" />
                            )}
                            <div>
                              <div className="text-white font-medium">{ranking.team.name}</div>
                              <div className={`text-xs ${ranking.team.is_active ? 'text-green-400' : 'text-red-400'}`}>
                                {ranking.team.is_active ? 'Active' : 'Inactive'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-white font-medium">{ranking.hltv_points}</td>
                        <td className="py-3 px-4">
                          <span className={`text-sm font-medium ${
                            ranking.ranking_change === '-' ? 'text-gray-400' :
                            ranking.ranking_change.startsWith('+') ? 'text-green-400' : 'text-red-400'
                          }`}>
                            {ranking.ranking_change}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-gray-300">{formatDate(ranking.ranking_date)}</td>
                        <td className="py-3 px-4 text-gray-400">{ranking.team.hltv_team_id}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {renderPagination(rankingsPagination, (page) => loadRankings(page, rankingsDate))}
            </>
          )}
        </motion.div>
      )}
        </div>
      </div>
    </>
  );
};

export default HltvAdminPage;
